import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import DialogHeader from "../components/DialogHeader"
import AmdSessionOptionsForm from "./AmdSessionOptionsForm"
import {
  amdFormBlocker,
  amdFormFromOptions,
  buildAmdSessionPayload,
} from "./amd"
import { dialableSummary, dialerSessionErrorMessage } from "./powerDialer"

const EMPTY_PREVIEW = {
  queue_count: 0,
  queue_capped: false,
  max_queue_size: 200,
  first_name: "",
  selected_count: 0,
  wrap_up_seconds: 3,
  dnc_skipped_count: 0,
  total_count: null,
  no_phone_count: 0,
}

export default function ConfirmStartPowerDialerDialog({
  open,
  source,
  filters,
  ordering,
  selectedIds,
  isStarting = false,
  onClose,
  onConfirm,
}) {
  const axios = useAuthorizedAxiosClient()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [preview, setPreview] = useState(EMPTY_PREVIEW)
  const [sessionName, setSessionName] = useState("")
  const [amdForm, setAmdForm] = useState(() => amdFormFromOptions(null))
  const selectedCount = Array.isArray(selectedIds) ? selectedIds.length : 0

  useEffect(() => {
    if (!open) {
      setError("")
      setPreview(EMPTY_PREVIEW)
      setSessionName("")
      setAmdForm(amdFormFromOptions(null))
      return undefined
    }

    let cancelled = false
    setIsLoading(true)
    setError("")
    axios
      .post("/crm/api/dialer/sessions/preview/", {
        source,
        filters: filters || [],
        ordering: ordering || "",
        selected_ids: selectedIds || [],
      })
      .then(({ data }) => {
        if (cancelled) return
        setPreview({
          ...EMPTY_PREVIEW,
          ...data,
        })
        setAmdForm(amdFormFromOptions(data?.amd))
      })
      .catch((err) => {
        if (cancelled) return
        setPreview(EMPTY_PREVIEW)
        const isCandidates = source === "candidates"
        const noun = isCandidates ? "candidates" : "leads"
        setError(
          dialerSessionErrorMessage(
            err,
            selectedCount
              ? `Could not count dialable ${noun} in this selection.`
              : `Could not count dialable ${noun} for this list.`
          )
        )
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [axios, filters, open, ordering, selectedCount, selectedIds, source])

  const isCandidates = source === "candidates"
  const itemNoun = isCandidates ? "candidate" : "lead"
  const queueCount = Number(preview.queue_count) || 0
  const wrapSeconds = Number(preview.wrap_up_seconds) || 3
  const trimmedName = sessionName.trim()
  const amdOptions = preview.amd || null
  const summary = dialableSummary(preview)
  const amdBlocker = amdFormBlocker(amdForm, amdOptions)
  const canStart =
    !isLoading &&
    !error &&
    queueCount > 0 &&
    !isStarting &&
    Boolean(trimmedName) &&
    !amdBlocker

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogHeader title="Start Power Dialer?" onClose={onClose} />
      <ConditionalPurpleLinearProgress visible={isLoading || isStarting} />
      <DialogContent>
        <Column gap={1.5}>
          <TextField
            autoFocus
            required
            fullWidth
            label="Session name"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            helperText="Required. This name appears in Activity → Power Dialing history."
            inputProps={{ maxLength: 128 }}
          />
          <Typography variant="body1">
            Power Dialer calls <strong>one {itemNoun} at a time</strong>
            {selectedCount
              ? ` from the ${itemNoun}s you checked`
              : isCandidates
              ? " from your current Candidates filters"
              : " from your current My Leads filters"}
            . When a call ends and you log the outcome, it waits{" "}
            <strong>{wrapSeconds} seconds</strong>, then dials the next number.
            You can pause, skip, or stop at any time.
          </Typography>
          {summary && !isLoading && !error ? (
            <Column gap={0.25}>
              <Typography variant="body1">
                <strong>{summary.headline}</strong>
              </Typography>
              {summary.breakdown ? (
                <Typography variant="body2" color="text.secondary">
                  {summary.breakdown}
                </Typography>
              ) : null}
            </Column>
          ) : null}
          {isLoading ? (
            <Typography variant="body2" color="text.secondary">
              {selectedCount
                ? `Counting dialable ${itemNoun}s in your selection…`
                : `Counting dialable ${itemNoun}s in this list…`}
            </Typography>
          ) : error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : queueCount === 0 ? (
            <Typography variant="body2">
              There are <strong>no dialable {itemNoun}s</strong>{" "}
              {selectedCount ? "in this selection" : "in this list"}. Add a
              phone number or{" "}
              {selectedCount
                ? `choose different ${itemNoun}s`
                : "change your filters"}
              , then try again.
            </Typography>
          ) : (
            <Typography variant="body1">
              This session will attempt{" "}
              <strong>{queueCount.toLocaleString()}</strong> {itemNoun}
              {queueCount === 1 ? "" : "s"}
              {selectedCount
                ? ` from ${selectedCount.toLocaleString()} selected`
                : preview.queue_capped
                ? ` (the first ${Number(
                    preview.max_queue_size || 200
                  ).toLocaleString()} matching ${itemNoun}s with a phone number)`
                : ""}
              {preview.first_name
                ? `, starting with ${preview.first_name}`
                : ""}
              .
            </Typography>
          )}
          {amdOptions ? (
            <AmdSessionOptionsForm
              options={amdOptions}
              value={amdForm}
              onChange={setAmdForm}
              disabled={isStarting}
            />
          ) : null}
          {!summary &&
          Number(preview.dnc_skipped_count) > 0 &&
          !isLoading &&
          !error ? (
            <Typography variant="body2" color="error">
              {Number(preview.dnc_skipped_count).toLocaleString()} DNC{" "}
              {itemNoun}
              {Number(preview.dnc_skipped_count) === 1 ? "" : "s"} will be left
              out.
            </Typography>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isStarting}
          disabled={!canStart}
          onClick={() =>
            onConfirm(trimmedName, buildAmdSessionPayload(amdForm))
          }
        >
          Start
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
